import { QueryClient, QueryFunction } from "@tanstack/react-query";

/**
 * O erro de API com STATUS e CODE, não só texto.
 *
 * ⚠️ `message` continua sendo `"403: {corpo}"` — é o que dezenas de `toast({
 * description: e.message })` já mostram, e mudar isso silenciaria todos eles. O
 * que muda é que agora existe `status`, `code` e `mensagem` (o campo `error` do
 * servidor, limpo) para quem precisa DECIDIR em cima do erro em vez de só exibi-lo:
 * é o que permite à tela distinguir "sua org não é dessa área" de "deu erro".
 */
export class ErroApi extends Error {
  readonly status: number;
  /** O `code` do corpo JSON (`ORG_TIPO_INVALIDO`, `PERFIL_INVALIDO`…), se houver. */
  readonly code: string | null;
  /** O `error` do corpo JSON — a frase escrita para a pessoa. */
  readonly mensagem: string;
  /**
   * O `estadoAtual` do 409 dos verbos do Manejo (iniciar/concluir/desfazer…): o
   * estado em que a operação JÁ está. Permite tratar "alguém já fez" — ou a resposta
   * anterior que se perdeu no sinal fraco — como sucesso, em vez de erro.
   */
  readonly estadoAtual: string | null;

  constructor(status: number, corpo: string) {
    super(`${status}: ${corpo}`);
    this.name = "ErroApi";
    this.status = status;

    // Corpo pode não ser JSON (502 de proxy, HTML de erro). Nunca deixar o parse
    // derrubar o tratamento do erro — seria um erro escondendo outro.
    let code: string | null = null;
    let mensagem = corpo;
    let estadoAtual: string | null = null;
    try {
      const json = JSON.parse(corpo) as { error?: unknown; code?: unknown; estadoAtual?: unknown };
      if (typeof json.code === "string") code = json.code;
      if (typeof json.estadoAtual === "string") estadoAtual = json.estadoAtual;
      if (typeof json.error === "string") mensagem = json.error;
    } catch {
      // Corpo não-JSON: `mensagem` fica com o texto cru, que é melhor que vazio.
      code = null;
    }
    this.code = code;
    this.mensagem = mensagem;
    this.estadoAtual = estadoAtual;
  }
}

async function throwIfResNotOk(res: Response) {
  if (!res.ok) {
    if (res.status === 402) {
      window.dispatchEvent(new CustomEvent('rizoma:trial-expired'));
    }
    const text = (await res.text()) || res.statusText;
    throw new ErroApi(res.status, text);
  }
}

export async function apiRequest(
  method: string,
  url: string,
  data?: unknown | undefined,
): Promise<Response> {
  const headers: Record<string, string> = {};

  if (data) {
    headers["Content-Type"] = "application/json";
  }

  // Nota: sem Authorization header — autenticação via cookie httpOnly (rizoma_session)
  // O browser envia o cookie automaticamente graças a credentials: "include"

  const res = await fetch(url, {
    method,
    headers,
    body: data ? JSON.stringify(data) : undefined,
    credentials: "include",
  });

  await throwIfResNotOk(res);
  return res;
}

/**
 * Fetch + checagem de status + json, para queryFns que usam query-string
 * (e por isso não podem usar o getQueryFn default que monta a URL pelo queryKey).
 * Lança em status não-2xx → react-query trata como erro e mantém o default (ex.: []),
 * em vez de devolver o objeto de erro como "dado" e quebrar em .filter/.map.
 */
export async function fetchJson<T = unknown>(url: string): Promise<T> {
  const res = await fetch(url, { credentials: "include" });
  await throwIfResNotOk(res);
  return (await res.json()) as T;
}

type UnauthorizedBehavior = "returnNull" | "throw";
export const getQueryFn: <T>(options: {
  on401: UnauthorizedBehavior;
}) => QueryFunction<T> =
  ({ on401: unauthorizedBehavior }) =>
  async ({ queryKey }) => {
    // Sem Authorization header — cookie httpOnly gerencia a sessão
    const res = await fetch(queryKey.join("/") as string, {
      credentials: "include",
    });

    if (unauthorizedBehavior === "returnNull" && res.status === 401) {
      return null;
    }

    await throwIfResNotOk(res);
    return await res.json();
  };

export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      queryFn: getQueryFn({ on401: "throw" }),
      refetchInterval: false,
      refetchOnWindowFocus: false,
      staleTime: Infinity,
      retry: false,
    },
    mutations: {
      retry: false,
    },
  },
});
